import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Alert,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  VideoLibrary as VideoIcon,
  Description as DescriptionIcon,
  Quiz as QuizIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon,
  Grade as GradeIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { fromBackendStudyPlanContent, toBackendStudyPlanContent, unitsToSyllabusPayload } from '../utils/curriculumMappers.ts';
import { useCurriculumAIPolling } from '../hooks/useDeliverablesPolling.ts';
import type { WeekPlanUI } from '../types/curriculum.types.ts';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import BoardClassSubjectSelector from './Boardclasssubjectselector.tsx';
import { useRecoilValue } from 'recoil';
import { curriculumSelectionAtom } from './Curriculumselectionatom.ts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Subtopic {
  subtopic: string;
  marks: number;
  prerequisites?: { name: string }[];
}

interface Topic {
  topic: string;
  description?: string;
  subtopics: Subtopic[];
  learning_material?: string[];
  topic_prerequisites?: { name: string }[];
}

interface KnowledgeMap {
  id?: string;
  _id?: string;
  board: string;
  class: string;
  subject: string;
  topics: Topic[];
  books?: string[];
}

interface WeekPlan extends WeekPlanUI { }

interface UnitTest {
  week: number;
  name: string;
  topics: string;
  totalMarks?: number;
}

// ─── Helper Functions ─────────────────────────────────────────────────────────

function generateWeekPlan(topics: Topic[]): WeekPlan[] {
  if (!topics || topics.length === 0) return [];

  return topics.map((topic, idx) => ({
    week: idx + 1,
    theme: topic.topic,
    topics: topic.subtopics?.map(st => st.subtopic) || [],
    classWork: [
      { type: 'Demo', title: `Understanding ${topic.topic}`, duration: '20 min' },
      { type: 'Problem Solving', title: `${topic.topic} Practice`, duration: '25 min' },
      { type: 'Discussion', title: `Q&A on ${topic.topic}`, duration: '15 min' },
    ],
    homework: [
      { title: `${topic.topic} Worksheet`, problems: topic.subtopics?.length * 5 || 10 },
      { title: `${topic.topic} Practice Problems`, problems: topic.subtopics?.length * 3 || 8 },
      { title: `${topic.topic} Research Task`, problems: topic.subtopics?.length * 2 || 5 },
    ],
    quiz: {
      type: 'Objective',
      questions: topic.subtopics?.length * 3 || 10,
      duration: `${Math.min(20 + (topic.subtopics?.length || 0) * 2, 45)} min`
    },
    resources: topic.learning_material || [],
    marks: topic.subtopics?.reduce((sum, st) => sum + (st.marks || 0), 0) || 0,
    description: topic.description,
  }));
}

function generateUnitTests(topics: Topic[]): UnitTest[] {
  if (!topics || topics.length === 0) return [];

  const tests: UnitTest[] = [];
  const topicsPerTest = Math.max(2, Math.ceil(topics.length / 3));

  for (let i = 0; i < topics.length; i += topicsPerTest) {
    const testTopics = topics.slice(i, Math.min(i + topicsPerTest, topics.length));
    const weekNumber = Math.min(i + topicsPerTest + 1, topics.length + 1);
    const totalMarks = testTopics.reduce((sum, t) => sum + (t.subtopics?.reduce((s, st) => s + (st.marks || 0), 0) || 0), 0);

    tests.push({
      week: weekNumber,
      name: testTopics.length === topics.length ? 'Final Assessment' : `Unit Test ${Math.floor(i / topicsPerTest) + 1}`,
      topics: testTopics.map(t => t.topic).join(', '),
      totalMarks: Math.ceil(totalMarks * 0.8) || 50,
    });
  }

  return tests;
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface WeekWiseStudyPlanProps {
  initialBoard?: string;
  initialClass?: string;
  initialSubject?: string;
  onProceedToGuidelines?: (board: string, classVal: string, subject: string) => void;
}

export default function WeekWiseStudyPlan({
  initialBoard,
  initialClass,
  initialSubject,
  onProceedToGuidelines,
}: WeekWiseStudyPlanProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();

  // State for API data
  const [knowledgeMaps, setKnowledgeMaps] = useState<KnowledgeMap[]>([]);
  const [loading, setLoading] = useState(false);

  const { board: selectedBoard, classVal: selectedClass, subject: selectedSubject } = useRecoilValue(curriculumSelectionAtom);

  const [currentKnowledgeMap, setCurrentKnowledgeMap] = useState<KnowledgeMap | null>(null);

  // UI state
  const [tabValue, setTabValue] = useState(0);
  const [weekPlans, setWeekPlans] = useState<WeekPlan[]>([]);
  const [planStatus, setPlanStatus] = useState<string | null>(null);
  const [currentPlanId, setCurrentPlanId] = useState<string | null>(null);
  const [syllabusId, setSyllabusId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [studyPlanPollEnabled, setStudyPlanPollEnabled] = useState(false);
  const [pollEnabled, setPollEnabled] = useState(false);

  const { status: studyPlanPollStatus, progress: studyPlanPollProgress } = useCurriculumAIPolling(
    currentPlanId,
    syllabusId,
    'study_plan',
    studyPlanPollEnabled
  );

  const { status: pollStatus, progress: pollProgress } = useCurriculumAIPolling(
    currentPlanId,
    null,
    'deliverables',
    pollEnabled
  );

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Study Plan' },
    ...(selectedBoard ? [{ label: selectedBoard }] : []),
    ...(selectedClass ? [{ label: `Class ${selectedClass}` }] : []),
    ...(selectedSubject ? [{ label: selectedSubject }] : []),
  ];

  // Fetch all knowledge maps on component mount
  useEffect(() => {
    fetchKnowledgeMaps();
  }, []);

  const fetchKnowledgeMaps = async () => {
    try {
      setLoading(true);
      const maps = await aiActions.getKnowledgeList();
      setKnowledgeMaps(maps || []);
    } catch (error) {
      console.error('Error fetching knowledge maps:', error);
      showSnackBar('Failed to load knowledge maps', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Update current knowledge map when selection changes
  useEffect(() => {
    if (selectedBoard && selectedClass && selectedSubject) {
      const found = knowledgeMaps.find(
        (km) => km.board === selectedBoard && km.class === selectedClass && km.subject === selectedSubject
      );
      setCurrentKnowledgeMap(found || null);
      setTabValue(0);
      setWeekPlans([]);
      setPlanStatus(null);
      setCurrentPlanId(null);
      setSyllabusId(null);
      setPollEnabled(false);

      if (found) {
        fetchPlanData(found.id ?? found._id ?? '', found.topics);
      }
    } else {
      setCurrentKnowledgeMap(null);
    }
  }, [selectedBoard, selectedClass, selectedSubject]);

  const fetchPlanData = async (knowledgeMapId: string, topics?: Topic[]) => {
    try {
      setLoading(true);
      const syllabusList = await aiActions.getSyllabusList(knowledgeMapId);
      if (!syllabusList?.length) {
        setSyllabusId(null);
        setCurrentPlanId(null);
        setPlanStatus(null);
        if (topics?.length) {
          setWeekPlans(generateWeekPlan(topics));
        }
        return;
      }

      const syllabus = syllabusList[0];
      setSyllabusId(syllabus._id);

      const plans = await aiActions.getStudyPlan(syllabus._id);
      if (!plans?.length) {
        setCurrentPlanId(null);
        setPlanStatus(null);
        if (topics?.length) {
          setWeekPlans(generateWeekPlan(topics));
        }
        return;
      }

      const plan = plans[0];
      setCurrentPlanId(plan._id);
      setPlanStatus(plan.status ?? null);

      if (plan.content) {
        const hydrated = fromBackendStudyPlanContent(plan.content);
        if (hydrated.length > 0) {
          setWeekPlans(hydrated);
        } else if (topics?.length) {
          setWeekPlans(generateWeekPlan(topics));
        }
      } else if (topics?.length) {
        setWeekPlans(generateWeekPlan(topics));
      }
    } catch (error) {
      console.error('Error fetching plan data:', error);
    } finally {
      setLoading(false);
    }
  };

  const unitTests = currentKnowledgeMap?.topics ? generateUnitTests(currentKnowledgeMap.topics) : [];
  const currentWeek = weekPlans[tabValue];
  const isApproved = planStatus === 'approved';

  const ensureSyllabus = async (): Promise<string> => {
    if (!currentKnowledgeMap) throw new Error('No knowledge map selected');

    const kmId = currentKnowledgeMap.id ?? currentKnowledgeMap._id ?? '';
    if (syllabusId) return syllabusId;

    const existing = await aiActions.getSyllabusList(kmId);
    if (existing?.length > 0) {
      setSyllabusId(existing[0]._id);
      return existing[0]._id;
    }

    const payload = unitsToSyllabusPayload([], kmId, {
      board: selectedBoard,
      class: selectedClass,
      subject: selectedSubject,
      status: 'draft',
    });
    const created = await aiActions.createSyllabus(payload);
    const newId = created?._id ?? created?.data?._id;
    if (!newId) throw new Error('Failed to create syllabus');
    setSyllabusId(newId);
    return newId;
  };

  // const handleSavePlan = async () => {
  //   if (!currentKnowledgeMap) return;

  //   const plansToSave = weekPlans.length > 0 ? weekPlans : generateWeekPlan(currentKnowledgeMap.topics);
  //   if (plansToSave.length === 0) {
  //     showSnackBar('No topics available to save a plan.', 'warning');
  //     return;
  //   }

  //   setGenerating(true);
  //   try {
  //     const resolvedSyllabusId = await ensureSyllabus();
  //     const content = toBackendStudyPlanContent(plansToSave);

  //     if (currentPlanId) {
  //       await aiActions.updateStudyPlan(currentPlanId, { content });
  //     } else {
  //       const payload = { syllabus_id: resolvedSyllabusId, content };
  //       const created = await aiActions.createStudyPlan(payload);
  //       const planId = created?._id ?? created?.data?._id;
  //       setCurrentPlanId(planId ?? null);
  //       setPlanStatus('draft');
  //     }

  //     setWeekPlans(plansToSave);
  //     showSnackBar('Study plan saved!', 'success');
  //   } catch (error) {
  //     console.error('Error saving study plan:', error);
  //     showSnackBar('Failed to save study plan.', 'error');
  //   } finally {
  //     setGenerating(false);
  //   }
  // };

  const handleGenerateWithAI = async () => {
    if (!currentKnowledgeMap) return;

    setAiGenerating(true);
    try {
      const resolvedSyllabusId = await ensureSyllabus();
      await aiActions.generateStudyPlanWithAI(resolvedSyllabusId);
      setStudyPlanPollEnabled(true);
      showSnackBar('AI study plan generation started...', 'info');
    } catch (error) {
      console.error('Error triggering AI study plan:', error);
      showSnackBar('Failed to start AI study plan generation.', 'error');
    } finally {
      setAiGenerating(false);
    }
  };

  // const handleRegenerateLocally = () => {
  //   if (!currentKnowledgeMap?.topics?.length) return;
  //   setWeekPlans(generateWeekPlan(currentKnowledgeMap.topics));
  //   showSnackBar('Local template plan generated. Click Save to persist.', 'info');
  // };

  const handleApprovePlan = async () => {
    if (!currentKnowledgeMap) return;

    try {
      let planId = currentPlanId;
      if (!planId) {
        const resolvedSyllabusId = syllabusId ?? (await ensureSyllabus());
        const plans = await aiActions.getStudyPlan(resolvedSyllabusId);
        if (!plans?.length) {
          showSnackBar('No draft study plan found to approve.', 'warning');
          return;
        }
        planId = plans[0]._id;
      }

      await aiActions.updateStudyPlan(planId, { status: 'approved' });
      setCurrentPlanId(planId);
      setPlanStatus('approved');
      setPollEnabled(true);
      showSnackBar('Study plan approved! Generating teacher notes and deliverables...', 'success');
    } catch (error) {
      console.error('Error approving study plan:', error);
      showSnackBar('Failed to approve study plan.', 'error');
    }
  };

  useEffect(() => {
    if (studyPlanPollStatus === 'complete' && syllabusId && currentKnowledgeMap) {
      setStudyPlanPollEnabled(false);
      showSnackBar('AI study plan generated successfully!', 'success');
      fetchPlanData(currentKnowledgeMap.id ?? currentKnowledgeMap._id ?? '', currentKnowledgeMap.topics);
    } else if (studyPlanPollStatus === 'timeout') {
      showSnackBar('AI generation is taking longer than expected. Try refreshing.', 'warning');
      setStudyPlanPollEnabled(false);
    }
  }, [studyPlanPollStatus, syllabusId, currentKnowledgeMap, showSnackBar]);

  useEffect(() => {
    if (pollStatus === 'complete') {
      showSnackBar('AI deliverables generated successfully!', 'success');
      setPollEnabled(false);
    } else if (pollStatus === 'timeout') {
      showSnackBar('Generation is taking longer than expected. Check back shortly.', 'warning');
      setPollEnabled(false);
    }
  }, [pollStatus, showSnackBar]);

  const handleSubmitForApproval = async () => {
    if (!currentPlanId) {
      showSnackBar('Save the study plan before submitting.', 'warning');
      return;
    }
    try {
      await aiActions.updateStudyPlan(currentPlanId, { status: 'submitted' });
      setPlanStatus('submitted');
      showSnackBar('Study plan submitted for coordinator review', 'success');
    } catch (error) {
      console.error('Error submitting study plan:', error);
      showSnackBar('Failed to submit study plan.', 'error');
    }
  };

  const handleResetApproval = () => {
    setPlanStatus(null);
    setPollEnabled(false);
    showSnackBar('Approval status reset locally', 'info');
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const handlePrevWeek = () => {
    if (tabValue > 0) setTabValue(tabValue - 1);
  };

  const handleNextWeek = () => {
    if (tabValue < weekPlans.length - 1) setTabValue(tabValue + 1);
  };

  const totalTopics = currentKnowledgeMap?.topics.length || 0;
  const totalSubtopics = currentKnowledgeMap?.topics.reduce((sum, t) => sum + (t.subtopics?.length || 0), 0) || 0;
  const totalMarks = currentKnowledgeMap?.topics.reduce((sum, t) => sum + (t.subtopics?.reduce((s, st) => s + (st.marks || 0), 0) || 0), 0) || 0;

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 1.5, md: 2 }, 
      bgcolor: '#F5F7FA', 
      minHeight: '100vh',
      paddingLeft: { md: '160px', sm: '100%' },
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: { xs: 1, sm: 1.5 }, 
        flexWrap: 'wrap', 
        gap: 1 
      }}>
        <DynamicBreadcrumbs items={breadcrumbItems} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
          onClick={fetchKnowledgeMaps}
          disabled={loading}
          sx={{ 
            fontSize: { xs: '8px', sm: '10px' }, 
            minHeight: 28, 
            py: 0,
            px: { xs: 1, sm: 1.5 },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Selection Panel */}
      <BoardClassSubjectSelector
        externalLoading={loading}
        colProps={{ xs: 12, sm: 3 }}
      />

      {/* No Selection Message */}
      {!loading && (!selectedBoard || !selectedClass || !selectedSubject) && (
        <Alert 
          severity="info" 
          sx={{ 
            fontSize: { xs: '9px', sm: '10px' }, 
            py: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 1.5 },
            '& .MuiAlert-message': { fontSize: { xs: '9px', sm: '10px' } }
          }}
        >
          Select a Board, Class, and Subject to view the study plan
        </Alert>
      )}

      {/* No Data Message */}
      {!loading && selectedBoard && selectedClass && selectedSubject && !currentKnowledgeMap && (
        <Alert 
          severity="warning" 
          sx={{ 
            fontSize: { xs: '9px', sm: '10px' }, 
            py: { xs: 0.5, sm: 1 }, 
            mb: { xs: 1, sm: 1.5 },
            '& .MuiAlert-message': { fontSize: { xs: '9px', sm: '10px' } }
          }}
        >
          No study plan found for {selectedBoard} - Class {selectedClass} - {selectedSubject}
        </Alert>
      )}

      {/* Action bar — visible whenever KM is selected */}
      {currentKnowledgeMap && (
        <Paper 
          elevation={0} 
          variant="outlined" 
          sx={{ 
            p: { xs: 1, sm: 1.5 }, 
            mb: { xs: 1, sm: 2 }, 
            borderRadius: 2, 
            bgcolor: '#FFFFFF' 
          }}
        >
          {weekPlans.length === 0 && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: { xs: 1, sm: 1.5 }, 
                py: { xs: 0.5, sm: 0.5 }, 
                fontSize: { xs: '9px', sm: '10px' },
                '& .MuiAlert-message': { fontSize: { xs: '9px', sm: '10px' } }
              }}
            >
              No study plan yet. Generate one with AI or use a local template.
            </Alert>
          )}

          {(studyPlanPollEnabled || aiGenerating) && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: { xs: 1, sm: 1.5 }, 
                py: { xs: 0.5, sm: 0.5 },
                '& .MuiAlert-message': { fontSize: { xs: '9px', sm: '10px' } }
              }}
            >
              Generating AI study plan... {studyPlanPollProgress}%
              <LinearProgress variant="determinate" value={studyPlanPollProgress} sx={{ mt: 0.5 }} />
            </Alert>
          )}

          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 0.5, sm: 1 }}
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleGenerateWithAI}
              disabled={generating || aiGenerating || studyPlanPollEnabled}
              sx={{ 
                fontSize: { xs: '8px', sm: '10px' }, 
                minHeight: { xs: 28, sm: 32 },
                flex: { xs: '1 1 auto', sm: '0 1 auto' },
              }}
            >
              Generate with AI
            </Button>
            {/* <Button
              variant="outlined"
              size="small"
              onClick={handleRegenerateLocally}
              disabled={generating || aiGenerating || studyPlanPollEnabled}
              sx={{ 
                fontSize: { xs: '8px', sm: '10px' }, 
                minHeight: { xs: 28, sm: 32 },
                flex: { xs: '1 1 auto', sm: '0 1 auto' },
              }}
            >
              Regenerate locally
            </Button> */}
            {/* <Button
              variant="outlined"
              size="small"
              onClick={handleSavePlan}
              disabled={generating || weekPlans.length === 0}
              sx={{ 
                fontSize: { xs: '8px', sm: '10px' }, 
                minHeight: { xs: 28, sm: 32 },
                flex: { xs: '1 1 auto', sm: '0 1 auto' },
              }}
            >
              Save Plan
            </Button> */}
            {onProceedToGuidelines && weekPlans.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => onProceedToGuidelines(selectedBoard, selectedClass, selectedSubject)}
                sx={{ 
                  fontSize: { xs: '8px', sm: '10px' }, 
                  minHeight: { xs: 28, sm: 32 },
                  flex: { xs: '1 1 auto', sm: '0 1 auto' },
                }}
              >
                Proceed to Guidelines
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {currentKnowledgeMap && currentKnowledgeMap.topics && weekPlans.length > 0 && (
        <>
          {/* Approval Status Banner */}
          <Alert
            severity={isApproved ? 'success' : 'warning'}
            sx={{ 
              mb: { xs: 1, sm: 1.5 }, 
              py: { xs: 0.5, sm: 0.5 },
              '& .MuiAlert-message': { fontSize: { xs: '9px', sm: '10px' } },
              '& .MuiAlert-action': { alignItems: 'center', py: 0 }
            }}
            icon={isApproved ? <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 18 } }} /> : <PendingIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
            action={
              !isApproved ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5}>
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={handleSubmitForApproval} 
                    sx={{ 
                      fontSize: { xs: '8px', sm: '9px' }, 
                      minHeight: { xs: 24, sm: 28 },
                      px: { xs: 1, sm: 1.5 }
                    }}
                  >
                    Submit
                  </Button>
                  <Button 
                    color="inherit" 
                    size="small" 
                    variant="outlined" 
                    onClick={handleApprovePlan} 
                    sx={{ 
                      fontSize: { xs: '8px', sm: '9px' }, 
                      minHeight: { xs: 24, sm: 28 },
                      px: { xs: 1, sm: 1.5 }
                    }}
                  >
                    Approve
                  </Button>
                </Stack>
              ) : (
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleResetApproval} 
                  sx={{ 
                    fontSize: { xs: '8px', sm: '9px' }, 
                    minHeight: { xs: 24, sm: 28 },
                    px: { xs: 1, sm: 1.5 }
                  }}
                >
                  Reset
                </Button>
              )
            }
          >
            {isApproved
              ? '✓ Study Plan Approved! Teachers can now access the plan.'
              : '⏳ Study Plan pending approval from Academic Coordinator'}
          </Alert>

          {pollEnabled && pollStatus === 'polling' && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: { xs: 1, sm: 1.5 }, 
                py: { xs: 0.5, sm: 0.5 },
                '& .MuiAlert-message': { fontSize: { xs: '9px', sm: '10px' } }
              }}
            >
              Generating teacher notes and deliverables... {pollProgress}%
              <LinearProgress variant="determinate" value={pollProgress} sx={{ mt: 0.5 }} />
            </Alert>
          )}

          {/* Summary Stats - Mobile Responsive */}
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 1.5, sm: 2 } }}>
            {[
              { label: 'Weeks', value: weekPlans.length, color: '#E3F2FD', textColor: '#1565C0', icon: <CalendarIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> },
              { label: 'Topics', value: totalTopics, color: '#F3E5F5', textColor: '#7B1FA2', icon: <MenuBookIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> },
              { label: 'Subtopics', value: totalSubtopics, color: '#E8F5E9', textColor: '#2E7D32', icon: <MenuBookIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> },
              { label: 'Total Marks', value: totalMarks, color: '#FFF8E1', textColor: '#F57F17', icon: <GradeIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> },
              { label: 'Unit Tests', value: unitTests.length, color: '#FFEBEE', textColor: '#C62828', icon: <AssignmentIcon sx={{ fontSize: { xs: 14, sm: 18 } }} /> },
            ].map(s => (
              <Grid item xs={6} sm={4} md={2.4} key={s.label}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    textAlign: 'center',
                    borderRadius: 2,
                    bgcolor: s.color,
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {s.icon}
                    <Typography variant="h5" sx={{ fontWeight: 800, color: s.textColor, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ 
                    fontWeight: 600, 
                    color: 'text.secondary', 
                    textTransform: 'uppercase', 
                    letterSpacing: 0.5,
                    fontSize: { xs: '0.5rem', sm: '0.65rem' },
                    mt: 0.5,
                  }}>
                    {s.label}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Week Navigation - Mobile Friendly */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: { xs: 1, sm: 1.5 },
            gap: 1,
          }}>
            {isMobile ? (
              // Mobile: Previous/Next buttons with week indicator
              <>
                <IconButton 
                  size="small" 
                  onClick={handlePrevWeek} 
                  disabled={tabValue === 0}
                  sx={{ border: '1px solid #E2E8F0', borderRadius: 1 }}
                >
                  <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Week {tabValue + 1} of {weekPlans.length}: {weekPlans[tabValue]?.theme?.substring(0, 20)}...
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={handleNextWeek} 
                  disabled={tabValue === weekPlans.length - 1}
                  sx={{ border: '1px solid #E2E8F0', borderRadius: 1 }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </>
            ) : (
              // Desktop/Tablet: Tabs
              <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid', borderColor: 'divider', width: '100%' }}>
                <Tabs
                  value={tabValue}
                  onChange={(e, v) => handleTabChange(v)}
                  variant={isTablet ? 'scrollable' : 'standard'}
                  scrollButtons="auto"
                  sx={{ minHeight: { xs: 36, sm: 40 } }}
                >
                  {weekPlans.map((plan, idx) => (
                    <Tab
                      key={idx}
                      label={`Week ${plan.week}`}
                      sx={{ 
                        fontSize: { xs: '8px', sm: '10px' }, 
                        minHeight: { xs: 36, sm: 40 },
                        px: { xs: 1, sm: 2 },
                      }}
                    />
                  ))}
                </Tabs>
              </Paper>
            )}
          </Box>

          {/* Week Content - Mobile Responsive */}
          {currentWeek && (
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              {/* Left Column - Learning Activities */}
              <Grid item xs={12} md={8}>
                {/* Class Work Structure */}
                <Card elevation={0} variant="outlined" sx={{ mb: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 700, 
                      color: '#1976d2',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      📖 Class Work Structure
                    </Typography>
                    <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
                    <Grid container spacing={{ xs: 1, sm: 2 }}>
                      {currentWeek.classWork.map((work, idx) => (
                        <Grid item xs={12} sm={4} key={idx}>
                          <Paper
                            elevation={0}
                            sx={{ 
                              p: { xs: 1, sm: 1.5 }, 
                              bgcolor: '#F8F9FA', 
                              borderRadius: 1.5, 
                              borderLeft: '4px solid #1976d2',
                              height: '100%',
                            }}
                          >
                            <Typography variant="caption" sx={{ 
                              fontWeight: 700, 
                              color: '#1976d2', 
                              textTransform: 'uppercase',
                              fontSize: { xs: '0.55rem', sm: '0.65rem' }
                            }}>
                              {work.type}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              mt: 0.5, 
                              mb: 1, 
                              fontWeight: 500,
                              fontSize: { xs: '0.7rem', sm: '0.8rem' }
                            }}>
                              {work.title}
                            </Typography>
                            <Chip 
                              label={work.duration} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                              sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: 8, sm: 10 } }} 
                            />
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Homework */}
                <Card elevation={0} variant="outlined" sx={{ mb: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 700, 
                      color: '#2E7D32',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      📝 Homework & Practice Tasks
                    </Typography>
                    <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
                    {currentWeek.homework.map((hw, idx) => (
                      <Box key={idx}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          p: { xs: 0.75, sm: 1 },
                          flexWrap: 'wrap',
                          gap: 0.5,
                          '&:hover': { bgcolor: '#F1F8E9', borderRadius: 1 }
                        }}>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 500,
                            fontSize: { xs: '0.7rem', sm: '0.8rem' }
                          }}>
                            {hw.title}
                          </Typography>
                          <Chip 
                            label={`${hw.problems} problems`} 
                            size="small" 
                            sx={{ 
                              bgcolor: '#E8F5E9', 
                              color: '#2E7D32', 
                              fontWeight: 600,
                              height: { xs: 18, sm: 20 },
                              fontSize: { xs: 8, sm: 10 }
                            }} 
                          />
                        </Box>
                        {idx < currentWeek.homework.length - 1 && <Divider sx={{ my: 0.5 }} />}
                      </Box>
                    ))}
                  </CardContent>
                </Card>

                {/* Resources */}
                <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 700, 
                      color: '#F57F17',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      📹 Learning Resources
                    </Typography>
                    <Divider sx={{ mb: { xs: 1, sm: 2 } }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
                      {currentWeek.resources && currentWeek.resources.length > 0 ? (
                        currentWeek.resources.slice(0, 5).map((resource, idx) => (
                          <Chip
                            key={idx}
                            icon={resource.toLowerCase().includes('video') ? <VideoIcon sx={{ fontSize: { xs: 12, sm: 14 } }} /> : <DescriptionIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                            label={resource.length > 20 ? resource.substring(0, 20) + '...' : resource}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              height: { xs: 22, sm: 24 }, 
                              fontSize: { xs: 9, sm: 11 }, 
                              bgcolor: '#FFFDE7', 
                              borderColor: '#FFF59D',
                              maxWidth: { xs: '100%', sm: 'auto' },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>
                          No resources available
                        </Typography>
                      )}
                      {currentWeek.resources && currentWeek.resources.length > 5 && (
                        <Chip 
                          label={`+${currentWeek.resources.length - 5} more`} 
                          size="small" 
                          variant="outlined" 
                          sx={{ height: { xs: 22, sm: 24 }, fontSize: { xs: 9, sm: 11 } }} 
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Assessments */}
              <Grid item xs={12} md={4}>
                {/* Quiz */}
                <Card elevation={0} variant="outlined" sx={{ 
                  mb: { xs: 1.5, sm: 2 }, 
                  bgcolor: '#E3F2FD', 
                  borderRadius: 2, 
                  border: '1px solid #90CAF9' 
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1 }, 
                      fontWeight: 700, 
                      color: '#1565C0',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      🎯 Weekly Quiz
                    </Typography>
                    <Typography variant="h3" sx={{ 
                      textAlign: 'center', 
                      my: { xs: 1, sm: 2 }, 
                      fontWeight: 800, 
                      color: '#1565C0',
                      fontSize: { xs: '2rem', sm: '3rem' }
                    }}>
                      {currentWeek.quiz.questions}
                    </Typography>
                    <Typography variant="body2" display="block" textAlign="center" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.7rem', sm: '0.8rem' }
                    }}>
                      Questions
                    </Typography>
                    <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ 
                      mt: 0.5,
                      fontSize: { xs: '0.6rem', sm: '0.7rem' }
                    }}>
                      Type: {currentWeek.quiz.type}
                    </Typography>
                    <Typography variant="caption" display="block" textAlign="center" color="text.secondary" sx={{ 
                      fontSize: { xs: '0.6rem', sm: '0.7rem' }
                    }}>
                      Duration: {currentWeek.quiz.duration}
                    </Typography>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      size="medium" 
                      sx={{ 
                        mt: { xs: 1, sm: 2 }, 
                        fontWeight: 700, 
                        borderRadius: 1.5, 
                        boxShadow: 2,
                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 0.75 },
                      }}
                    >
                      Take Quiz
                    </Button>
                  </CardContent>
                </Card>

                {/* Unit Tests */}
                <Card elevation={0} variant="outlined" sx={{ 
                  mb: { xs: 1.5, sm: 2 }, 
                  bgcolor: '#FFF3E0', 
                  borderRadius: 2, 
                  border: '1px solid #FFCC80' 
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 700, 
                      color: '#E65100',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      📊 Unit Tests
                    </Typography>
                    <Divider sx={{ mb: { xs: 1, sm: 1.5 }, borderColor: '#FFE0B2' }} />
                    {unitTests.map((test, idx) => (
                      <Paper 
                        elevation={0} 
                        key={idx} 
                        sx={{ 
                          mb: 1, 
                          p: { xs: 1, sm: 1.5 }, 
                          bgcolor: '#FFF8E1', 
                          borderRadius: 1.5, 
                          border: '1px dashed #FFB74D' 
                        }}
                      >
                        <Typography variant="body2" sx={{ 
                          fontWeight: 700, 
                          color: '#E65100',
                          fontSize: { xs: '0.7rem', sm: '0.8rem' }
                        }}>
                          {test.name}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ 
                          fontSize: { xs: '0.55rem', sm: '0.65rem' }
                        }}>
                          Week {test.week} • Marks: {test.totalMarks || 50}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ 
                          mt: 0.5, 
                          fontStyle: 'italic',
                          fontSize: { xs: '0.5rem', sm: '0.6rem' }
                        }}>
                          Topics: {test.topics.substring(0, 40)}...
                        </Typography>
                      </Paper>
                    ))}
                    {unitTests.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        textAlign: 'center',
                        fontSize: { xs: '0.7rem', sm: '0.8rem' }
                      }}>
                        No unit tests scheduled
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Topics Covered */}
                <Card elevation={0} variant="outlined" sx={{ mb: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1.5 }, 
                      fontWeight: 700,
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      📚 Topics Covered
                    </Typography>
                    <Divider sx={{ mb: { xs: 1, sm: 1.5 } }} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
                      {currentWeek.topics.slice(0, 6).map((topic, idx) => (
                        <Chip 
                          key={idx} 
                          label={topic.length > 20 ? topic.substring(0, 20) + '...' : topic} 
                          size="small" 
                          variant="outlined" 
                          sx={{ 
                            height: { xs: 22, sm: 24 }, 
                            fontSize: { xs: 9, sm: 11 },
                            maxWidth: { xs: '100%', sm: 'auto' },
                          }} 
                        />
                      ))}
                      {currentWeek.topics.length > 6 && (
                        <Chip 
                          label={`+${currentWeek.topics.length - 6} more`} 
                          size="small" 
                          sx={{ 
                            height: { xs: 22, sm: 24 }, 
                            fontSize: { xs: 9, sm: 11 }, 
                            fontWeight: 600 
                          }} 
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>

                {/* Marks */}
                <Card elevation={0} variant="outlined" sx={{ 
                  mb: { xs: 1.5, sm: 2 }, 
                  bgcolor: '#E8F5E9', 
                  borderRadius: 2, 
                  border: '1px solid #A5D6A7' 
                }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 1, sm: 1 }, 
                      fontWeight: 700, 
                      color: '#2E7D32',
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      🏆 Assessment Marks
                    </Typography>
                    <Typography variant="h3" textAlign="center" color="#2E7D32" sx={{ 
                      my: { xs: 0.5, sm: 1 }, 
                      fontWeight: 800,
                      fontSize: { xs: '2rem', sm: '3rem' }
                    }}>
                      {currentWeek.marks}
                    </Typography>
                    <Typography variant="body2" textAlign="center" display="block" sx={{ 
                      fontWeight: 500, 
                      color: '#388E3C',
                      fontSize: { xs: '0.6rem', sm: '0.75rem' }
                    }}>
                      Total marks for this week
                    </Typography>
                  </CardContent>
                </Card>

                {/* Student Notes */}
                <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{ 
                      mb: { xs: 0.5, sm: 0.5 }, 
                      fontWeight: 700,
                      fontSize: { xs: '0.8rem', sm: '1rem' }
                    }}>
                      📔 Student Notes
                    </Typography>
                    <Typography variant="body2" color="text.secondary" display="block" sx={{ 
                      mb: { xs: 1, sm: 2 },
                      fontSize: { xs: '0.6rem', sm: '0.7rem' }
                    }}>
                      Structured self-study notes available.
                    </Typography>
                    <Button 
                      variant="outlined" 
                      size="medium" 
                      fullWidth 
                      sx={{ 
                        fontWeight: 600, 
                        borderRadius: 1.5,
                        fontSize: { xs: '0.65rem', sm: '0.8rem' },
                        py: { xs: 0.5, sm: 0.75 },
                      }}
                    >
                      Download Notes
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}